"use client";
import { clientLogger } from "@/utils/clientLogger";
import { useHeaderHeight } from "@/app/providers/navigationProvider";
import DownArrowIcon from "@/components/_icons/downArrowIcon";
import HeaderClockIcon from "@/components/_icons/headerClockIcon";
import { NavigationBar } from "@/components/navigationBar/navigationBar";
import type { NavigationBarProps } from "@/components/navigationBar/navigationBar";
import { CartDrawerHost } from "@/components/cartDrawer/cartDrawerHost";
import { ClearCartModal } from "@/components/clearCartModal/clearCartModal";
import { NavFragmentSlot } from "@/components/navFragmentSlot/navFragmentSlot";
import { NotAcceptingOrdersBanner } from "@/components/notAcceptingOrdersBanner/notAcceptingOrdersBanner";
import { Surcharge } from "@/components/surcharge/surcharge";
import { OrderType, ServiceType } from "@/constants/enum";
import getCustomerDetails from "@/dataLayer/getCustomerDetails";
import type { EarnedPerks } from "@/dataLayer/getCustomerDetails";
import type { GetMerchantPreferencesResponse } from "@/dataLayer/getMerchantPreferences";
import getDefaultAddress from "@/dataLayer/getDefaultAddress";
import { Address } from "@/dto/address";
import { Category } from "@/dto/category";
import {
  DayPart,
  DayPartMenuCategories,
  DayPartMenuCookie,
} from "@/dto/dayPartMenu";
import { Service } from "@/dto/service";
import { RestaurantCart } from "@/models/cart/restaurantCart";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import pom from "@/utils/pom";
import { canMerchantAcceptOrders } from "@/utils/scheduleOrderUtils";
import {
  getFromSessionStorage,
  removeFromSessionStorage,
  saveDayPartMenuCookie,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import useMediaQuery from "@/utils/useMediaQuery";
import clsx from "clsx";
import { throttle } from "lodash";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import { use, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { Toaster } from "react-hot-toast";
import { convertTimeStringToDate } from "@/utils/dateTimeUtils";
import {
  ROUTE_CHECKOUT,
  ROUTE_CONFIRMATION,
  ROUTE_CUSTOMER_PROFILE,
  ROUTE_GUEST_CHECKOUT,
  ROUTE_MY_APPOINTMENTS,
  ROUTE_MY_ORDERS,
  UNCATEGORISED_CATEGORY_ID,
} from "@/utils/constants";
import { useNativeActions } from "@/utils/useNativeActions";
import { useDayPartMenuHandlers } from "@/utils/hooks/useDayPartMenuHandlers";
import { useMerchantColor } from "@/utils/hooks/useMerchantColor";
import ChevronDownIcon from "../_icons/chevronDownIcon";
import ClockIcon from "../_icons/clockIcon";
import { CmaHeader } from "./cmaHeader";
import { ColoHeader } from "./coloHeader";
import { CookiesBanner } from "./cookiesBanner";
import { headerRegistry } from "./headerRegistry";
import { BusinessHoursModal } from "./hoursModal";
import { DudaHeader } from "./dudaHeader";

export interface HeaderProps {
  merchantLogo?: string;
  merchantName?: string;
  merchant: any;
  menu: any;
  deleteSession: any;
  retailCategories?: any;
  serviceCategories?: any;
  redirectToGiftCard: boolean;
  pickupTimePromise?: Promise<any>;
  dayPartMenus?: DayPart[];
  dayPartMenuCategories?: DayPartMenuCategories[];
  paymentConfigPromise?: Promise<any>;
  preferencesPromise?: Promise<any>;
  isDuda?: boolean;
  navigationFragmentEnabled?: boolean;
  restaurantMfeEnabled?: boolean;
  navFragmentHtml?: string;
}

interface TimeSlot {
  start: string;
  end: string;
}

interface HeaderNavigationProps {
  navigationFragmentEnabled?: boolean;
  navFragmentHtml?: string;
  isScrolledDown?: boolean;
  slug?: string;
  deleteSession: () => Promise<void>;
  isScanToOrderFlow?: boolean;
  customerPerks?: EarnedPerks[];
  merchantPreferences?: GetMerchantPreferencesResponse;
  navigationBarProps: NavigationBarProps;
  onDayPartMenuChange: (menu: DayPart | null) => void;
  isClearCartOpen: boolean;
  onClearCartClose: () => void;
  onConfirmation: () => void;
  tempMenu: { current: DayPart | null };
}

function HeaderNavigation({
  navigationFragmentEnabled,
  navFragmentHtml,
  isScrolledDown = false,
  slug,
  deleteSession,
  isScanToOrderFlow = false,
  customerPerks,
  merchantPreferences,
  navigationBarProps,
  onDayPartMenuChange,
  isClearCartOpen,
  onClearCartClose,
  onConfirmation,
  tempMenu,
}: Readonly<HeaderNavigationProps>) {
  const t = useTranslations("NavigationBar");
  const merchantColor = useMerchantColor();

  if (navigationFragmentEnabled && slug) {
    return (
      <>
        <ClearCartModal
          isOpen={isClearCartOpen}
          onRequestClose={onClearCartClose}
          merchantColor={merchantColor}
          addToCart={onConfirmation}
          actions={[
            {
              text: t("cancel"),
              onClick: onClearCartClose,
              type: "secondary",
            },
            {
              text: t("clearCartAndSwitchMenu"),
              onClick: onConfirmation,
              type: "primary",
            },
          ]}
          title={t("clearCartTitle")}
          message={t("clearCartMessage", {
            menuName: tempMenu.current?.menuName ?? "",
          })}
        />
        <NavFragmentSlot
          slug={slug}
          initialHtml={navFragmentHtml}
          isScrolledDown={isScrolledDown}
          customerPerks={customerPerks}
          merchantPreferences={merchantPreferences}
          deleteSession={deleteSession}
          isScanToOrderFlow={isScanToOrderFlow}
          onDayPartMenuChange={(id) =>
            onDayPartMenuChange(
              navigationBarProps.dayPartMenus?.find(
                (dayPart) => dayPart.id === id
              ) ?? null
            )
          }
        />
        <CartDrawerHost
          merchantPreferences={merchantPreferences}
          pickupTimePromise={navigationBarProps.pickupTimePromise}
          selectedMenu={navigationBarProps.selectedMenu}
        />
      </>
    );
  }

  return <NavigationBar {...navigationBarProps} />;
}

interface TimeSlotProps extends TimeSlot {
  abbreviation: string;
}

interface MainTimeSlotProps {
  todayHours: TimeSlot[];
  abbreviation: string;
  showHoursModal?: boolean;
}

const commonClasses = ["items-right", "cursor-pointer"].join(" ");

const TimeSlot = ({ start, end, abbreviation }: TimeSlotProps) => {
  const t = useTranslations("Header");
  const locale = useLocale();
  const format = useFormatter();

  const getLocalizedTimeData = (timeStr: string) => {
    const luxonTime = convertTimeStringToDate(timeStr, abbreviation);

    const formattedTime = format.dateTime(luxonTime.toJSDate(), {
      hour: "numeric",
      minute: "2-digit",
      timeZone: abbreviation,
    });

    const localizedTzLabel = luxonTime.setLocale(locale).offsetNameShort;

    return { formattedTime, localizedTzLabel };
  };

  const startData = getLocalizedTimeData(start);
  const endData = getLocalizedTimeData(end);

  return (
    <span className="whitespace-nowrap">
      {t("timeRange", {
        start: startData.formattedTime,
        end: endData.formattedTime,
        abbreviation: startData.localizedTzLabel as string,
      })}
    </span>
  );
};

const MainTimeSlot = ({
  todayHours,
  abbreviation,
  showHoursModal,
}: MainTimeSlotProps) => {
  const t = useTranslations("Header");
  return (
    <div className={`${commonClasses} flex justify-center sm:justify-end`}>
      <HeaderClockIcon />
      <span className="font-medium px-1">{t("openToday")}</span>
      <TimeSlot
        start={todayHours[0].start}
        end={todayHours[0].end}
        abbreviation={abbreviation}
      />

      {showHoursModal && <DownArrowIcon />}
    </div>
  );
};

const AdditionalTimeSlots = ({
  todayHours,
  abbreviation,
  showHoursModal,
}: MainTimeSlotProps) => {
  return (
    <div className={`${commonClasses} ${showHoursModal ? "pr-5" : ""}`}>
      {todayHours.slice(1, 3).map((timeSlot: TimeSlot) => (
        <div key={timeSlot.start} className="whitespace-nowrap">
          <TimeSlot
            start={timeSlot.start}
            end={timeSlot.end}
            abbreviation={abbreviation}
          />
        </div>
      ))}
    </div>
  );
};

const MoreIndicator = () => {
  return (
    <div className={`${commonClasses} pr-5`}>
      <span className="ml-1 text-blue-600 hover:text-blue-800">...</span>
    </div>
  );
};

export const SimpleStorefrontHours = ({
  merchant,
  todayHours,
  abbreviation,
  headerBusinessHours,
  merchantPaymentConfig,
  showHoursModal = true,
}: any) => {
  const t = useTranslations("Header");
  const [hoursModalIsOpen, setHoursModalIsOpen] = useState<boolean>(false);

  const handleHoursModal = () => {
    setHoursModalIsOpen(!hoursModalIsOpen);
  };

  const hasHours = todayHours.length > 0;

  return (
    <div className={"sm:flex flex-row justify-center items-right"}>
      <div className="text-center">
        <div className={`${commonClasses} flex flex-col pb-2`}>
          {hasHours ? (
            <button
              onClick={handleHoursModal}
              aria-haspopup="dialog"
              aria-pressed={hoursModalIsOpen}
              className="sm:text-right"
              type="button"
              aria-label={t("openBusinessHoursAlt")}
            >
              <MainTimeSlot
                todayHours={todayHours}
                abbreviation={abbreviation}
                showHoursModal={showHoursModal}
              />
              {todayHours.length > 1 && (
                <AdditionalTimeSlots
                  todayHours={todayHours}
                  abbreviation={abbreviation}
                  showHoursModal={showHoursModal}
                />
              )}
              {todayHours.length > 3 && <MoreIndicator />}
            </button>
          ) : (
            <button
              className="flex justify-end font-medium px-1 pr-5 gap-[6px] items-center"
              onClick={handleHoursModal}
              aria-haspopup="dialog"
              aria-pressed={hoursModalIsOpen}
              type="button"
              aria-label={t("openBusinessHoursAlt")}
            >
              <ClockIcon height={24} width={24} />
              <span className="text-[15px] leading-6 font-bold">
                {t("closed")}
              </span>
              {showHoursModal && <ChevronDownIcon />}
            </button>
          )}
          {merchantPaymentConfig &&
            merchantPaymentConfig.surcharging?.rate > 0 && (
              <Surcharge
                isTimeSlot={true}
                surchargeRate={merchantPaymentConfig.surcharging?.rate}
              />
            )}
        </div>
      </div>
      <BusinessHoursModal
        isOpen={hoursModalIsOpen}
        onRequestClose={handleHoursModal}
        businessHours={headerBusinessHours}
        timezone={abbreviation}
        isRestaurantMerchant={merchant?.isRestaurant}
      />
    </div>
  );
};

const findPreSelectedMenu = (
  dayPartMenus: DayPart[],
  menuName: string | null,
  isDineIn: boolean
): DayPart | undefined => {
  if (!menuName || isDineIn) return undefined;
  return dayPartMenus.find((menu) => menu.formattedName === menuName);
};

const findSessionMenu = (
  dayPartMenus: DayPart[],
  isDineIn: boolean
): DayPart | undefined => {
  if (isDineIn) return undefined;
  const sessionDayPartMenu = getFromSessionStorage<{
    id?: string;
    menuId?: string;
  }>(SessionStorageKey.DAYPART_MENU, {});
  if (!sessionDayPartMenu?.id) return undefined;
  return dayPartMenus.find((menu) => menu.id === sessionDayPartMenu.id);
};

export const calculateCurrentDayPart = (
  isDineIn: boolean,
  dayPartMenus: DayPart[] = [],
  menuName: string | null = null
): DayPart | null => {
  if (!dayPartMenus || dayPartMenus.length === 0) return null;

  const preSelected = findPreSelectedMenu(dayPartMenus, menuName, isDineIn);
  if (preSelected) return preSelected;

  const sessionMenu = findSessionMenu(dayPartMenus, isDineIn);
  if (sessionMenu) return sessionMenu;

  const activeMenu = dayPartMenus.find((menu) => menu.activeNow);
  if (activeMenu) return activeMenu;

  const upcomingMenu = dayPartMenus.find((menu) => menu.upcoming);
  if (upcomingMenu) return upcomingMenu;

  return dayPartMenus[0];
};

// Extract: Derives whether page is preview/standalone site
export const isPreviewOrStandalone = (merchant: any): boolean => {
  const isPreviewSite =
    globalThis.window?.location?.hostname?.startsWith("preview-") ?? false;
  return merchant?.isStandaloneGiftCardWebsite && !isPreviewSite;
};

// Extract: Derives order/menu information from URL params
const getOrderContext = (searchParams: URLSearchParams | null) => {
  const orderType = searchParams?.get("order_type");
  const menuName = searchParams?.get("menu");
  const isDineIn = orderType?.toLowerCase() === OrderType.DINE_IN.toLowerCase();
  return { orderType, menuName, isDineIn };
};

// Extract: Derives table/scan-to-order state from local storage
const getScanToOrderContext = () => {
  const tableData = getFromLocalStorage(LocalStorageKey.TABLE_DATA, undefined);
  const scanToOrderInfo = getFromLocalStorage(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    {
      tableId: "",
      employeeId: "",
      orderType: OrderType.PICKUP,
    }
  );
  return {
    tableData,
    scanToOrderInfo,
    isScanToOrder: tableData && scanToOrderInfo,
  };
};

// Extract: Derives header business hours based on merchant type
const getHeaderBusinessHours = (merchant: any): any => {
  const service = merchant?.services?.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  return merchant?.isRestaurant
    ? service?.hours
    : merchant?.businessHours?.hours;
};

export const Header = ({
  merchantLogo,
  merchantName,
  merchant,
  menu,
  deleteSession,
  retailCategories,
  serviceCategories,
  pickupTimePromise,
  redirectToGiftCard,
  dayPartMenus,
  dayPartMenuCategories,
  paymentConfigPromise,
  preferencesPromise,
  isDuda,
  navigationFragmentEnabled = false,
  restaurantMfeEnabled,
  navFragmentHtml,
}: HeaderProps) => {
  const [cookies] = useCookies(["daypartMenu"]);
  const tPickup = useTranslations("CalculatePickTime");
  const tUncategorized = useTranslations("category");
  const { isCmaApp } = useNativeActions();

  const pathName = usePathname();
  const searchParams = useSearchParams();
  const [customerDetails, setCustomerDetails] = useState<any>(null);
  const [defaultAddress, setDefaultAddress] = useState<Address | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { menuName, isDineIn } = getOrderContext(searchParams);
  const isStandaloneGiftCard = isPreviewOrStandalone(merchant);
  const { isScanToOrder } = getScanToOrderContext();

  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState<
    boolean | undefined
  >(false);
  const isDayPartsEnabled = merchant?.enabledSettings?.includes(
    "OLO_MENUS_DAY_PART_ENABLED"
  );
  const [selectedMenu, setSelectedMenu] = useState<DayPart | null>(null);
  const [isClearCartOpen, setIsClearCartOpen] = useState(false);

  const scrollRef = useRef(0);
  const tempMenu = useRef<DayPart | null>(null);
  const isUserInitiatedDayPartSwitch = useRef(false);
  const { headerContentRef, recalculateHeight } = useHeaderHeight();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");

  const currentDay = new Date()
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();
  const headerBusinessHours = getHeaderBusinessHours(merchant);

  const merchantPaymentConfig =
    paymentConfigPromise && use(paymentConfigPromise);

  const merchantPreferences = preferencesPromise
    ? use(preferencesPromise)
    : null;

  const { onDayPartMenuChange, onConfirmation } = useDayPartMenuHandlers({
    isDineIn,
    restaurantCartLength: RestaurantCart.getItems().length,
    selectedMenu,
    searchParams,
    setIsClearCartOpen,
    setSelectedMenu,
    tempMenu,
    isUserInitiatedDayPartSwitch,
    isDayPartsEnabled,
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const [address, details] = await Promise.all([
          getDefaultAddress(),
          getCustomerDetails(merchant.id),
        ]);
        setDefaultAddress(address);
        setCustomerDetails(details);
      } catch (error) {
        clientLogger.error("Failed to fetch initial data", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [merchant.id]);

  useEffect(() => {
    if (!isDayPartsEnabled) {
      return;
    }

    const currentDayPart = calculateCurrentDayPart(
      isDineIn,
      dayPartMenus,
      menuName
    );

    if (currentDayPart) {
      const payload: DayPartMenuCookie = {
        id: currentDayPart.id,
        menuId: currentDayPart.menuId,
        name: currentDayPart.formattedName,
      };
      saveToSessionStorage(SessionStorageKey.DAYPART_MENU, payload);

      if (cookies?.daypartMenu?.id !== currentDayPart.id) {
        saveDayPartMenuCookie(payload);
      }
    }

    setSelectedMenu(currentDayPart);
  }, [isDayPartsEnabled, menuName, dayPartMenus]);

  useEffect(() => {
    const protectedPaths = [
      ROUTE_GUEST_CHECKOUT,
      ROUTE_CHECKOUT,
      ROUTE_CONFIRMATION,
      ROUTE_CUSTOMER_PROFILE,
      ROUTE_MY_ORDERS,
      ROUTE_MY_APPOINTMENTS,
    ];

    if (protectedPaths.includes(pathName)) {
      return;
    }
    const handleBeforeUnload = () => {
      const scheduledTime = getFromSessionStorage<{
        orderType?: OrderType;
      } | null>(SessionStorageKey.SCHEDULED_TIME, null);
      if (scheduledTime?.orderType == null) {
        removeFromSessionStorage(SessionStorageKey.SCHEDULED_TIME);
      } else {
        saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
          orderType: scheduledTime.orderType,
        });
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [pathName]);

  // To provide shrinking animation
  useEffect(() => {
    // Don't apply scroll effect on standalone gift card sites
    if (isStandaloneGiftCard) return;

    const node = document.querySelector("main");

    const handleScroll = () => {
      const scrollTop = node?.scrollTop || 0;
      const offset = 25;

      if (scrollTop > scrollRef.current + offset) {
        setIsScrolledDown(true);
        scrollRef.current = scrollTop;
      } else if (scrollTop < scrollRef.current - offset || scrollTop === 0) {
        setIsScrolledDown(false);
        scrollRef.current = scrollTop;
      }
    };

    const throttledHandleScroll = throttle(handleScroll, 450, {
      leading: true,
      trailing: true,
    });

    node?.addEventListener("scroll", throttledHandleScroll);

    return () => {
      node?.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [isStandaloneGiftCard]);

  const renameUncategorized = (cat: Category) =>
    cat.id === UNCATEGORISED_CATEGORY_ID
      ? { ...cat, name: tUncategorized("uncategorized") }
      : cat;

  const renamedServiceCategories = useMemo(
    () => serviceCategories.map(renameUncategorized),
    [serviceCategories]
  );

  const renamedRetailCategories = useMemo(
    () => retailCategories.map(renameUncategorized),
    [retailCategories]
  );

  // Use  headerRegistry to resolve special headers for routes (dependency injection / service locator)
  let routeRenderer = headerRegistry[pathName ?? ""];
  if (typeof routeRenderer === "function") {
    routeRenderer = isDuda
      ? () => <DudaHeader merchant={merchant} />
      : routeRenderer;
    const rendered = routeRenderer({
      pathName,
      searchParams,
      isScanToOrder,
      redirectToGiftCard,
      merchant,
    });
    if (rendered !== undefined) return rendered;
  }

  const todayHours = headerBusinessHours?.[currentDay] ?? [];

  const isAcceptingOrders = canMerchantAcceptOrders(
    merchant,
    isDineIn,
    selectedMenu,
    tPickup
  );

  const isNotAcceptingOrder = !isAcceptingOrders || !merchant.isCheckoutEnabled;

  if (isCmaApp) {
    return (
      <>
        <Toaster />
        <CmaHeader
          todaysHours={todayHours}
          businessHours={headerBusinessHours}
          merchantPaymentConfig={merchantPaymentConfig}
          isNotAcceptingOrders={isNotAcceptingOrder}
          selectedMenu={selectedMenu}
          pickupTimePromise={pickupTimePromise}
          isLoading={isLoading}
          defaultAddress={defaultAddress}
          retailCategories={renamedRetailCategories}
          serviceCategories={renamedServiceCategories}
          setIsCategoryMenuOpen={setIsCategoryMenuOpen}
          isCategoryMenuOpen={isCategoryMenuOpen}
          dayPartMenu={dayPartMenus}
          dayPartMenuCategory={dayPartMenuCategories}
          setSelectedMenu={setSelectedMenu}
          menu={menu}
          customerDetails={customerDetails}
          merchantPreferences={merchantPreferences}
        />
      </>
    );
  }

  const navigationBarProps: NavigationBarProps = {
    menu,
    customerPerks: customerDetails?.earnedPerks,
    merchantPreferences,
    deleteSession,
    retailCategories: renamedRetailCategories,
    serviceCategories: renamedServiceCategories,
    pickupTimePromise,
    defaultAddress,
    isLoading,
    setIsCategoryMenuOpen,
    isCategoryMenuOpen,
    dayPartMenus,
    dayPartMenuCategories,
    selectedMenu,
    setSelectedMenu,
    isNotAcceptingOrder,
    isScrolledDown,
    isDuda,
    restaurantMfeEnabled,
  };

  return (
    <>
      <Toaster />
      <nav
        className={clsx(
          "flex",
          "flex-col",
          "items-center",
          "w-full",
          "top-0",
          "z-10",
          "bg-[#FBFBFB]",
          {
            "pt-3": !isDuda && !isMobileScreen && isScrolledDown,
            absolute: headerContentRef.current,
          }
        )}
        ref={headerContentRef}
        data-testid={applyDataTestId(pom.header.headerContainer)}
      >
        <CookiesBanner />
        {isDuda ? (
          <DudaHeader
            merchant={merchant}
            paymentConfigPromise={paymentConfigPromise}
          />
        ) : (
          <ColoHeader
            merchantLogo={merchantLogo}
            merchantName={merchantName}
            merchant={merchant}
            paymentConfigPromise={paymentConfigPromise}
            isDuda={isDuda}
            recalculateHeight={recalculateHeight}
            isScrolledDown={isScrolledDown}
            selectedMenu={selectedMenu}
            restaurantMfeEnabled={restaurantMfeEnabled}
          />
        )}
        {pathName !== ROUTE_CONFIRMATION && !isStandaloneGiftCard && (
          <div
            className={clsx("w-full clover-body-color", {
              "pt-3": !isDuda && !isMobileScreen && isScrolledDown,
              "clover-bg-color": isDuda,
            })}
          >
            <HeaderNavigation
              navigationFragmentEnabled={navigationFragmentEnabled}
              navFragmentHtml={navFragmentHtml}
              isScrolledDown={isScrolledDown}
              slug={merchant?.slug}
              deleteSession={deleteSession}
              isScanToOrderFlow={Boolean(isScanToOrder)}
              customerPerks={customerDetails?.earnedPerks}
              merchantPreferences={merchantPreferences}
              navigationBarProps={navigationBarProps}
              onDayPartMenuChange={onDayPartMenuChange}
              isClearCartOpen={isClearCartOpen}
              onClearCartClose={() => setIsClearCartOpen(false)}
              onConfirmation={onConfirmation}
              tempMenu={tempMenu}
            />
          </div>
        )}
        <NotAcceptingOrdersBanner
          merchant={merchant}
          isNotAcceptingOrder={isNotAcceptingOrder}
          selectedDayPart={selectedMenu}
        />
      </nav>
    </>
  );
};
